import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";

import { getFilterAppsState } from "@/entities/filterApps";
import { useDispatch, useSelector } from "react-redux";

import MagicModeButton from "@/widgets/magicModeButton/ui/MagicModeButton/MagicModeButton";
import FilterButton from "@/widgets/filter/ui/FilterButton/FilterButton";
import {
  filterAppsDataActions,
  getFilterAppsData,
} from "@/entities/filterAppsData";

import ExploreAppsStyles from "@/Views/Virtual/ExplorAppsNew.module.scss";

import {
  ACTIVE_VIEW_HOME,
  ACTIVE_VIEW_SEARCH,
  ACTIVE_VIEW_ANIMATION,
  ACTIVE_VIEW_WEB_ONBOARDING,
  ACTIVE_VIEW_SAVED_MODE,
} from "@/Views/constants";

import { useCurrentOrganization, useMe } from "@/Hooks";
import mixpanel from "@/shared/lib/mixpanel";
import { filterAppsActions } from "@/entities/filterApps";
import { OnboardingModal } from "@/Views/OnboardingModal";
import { useSearch } from "@/contexts/SearchContext";

import SearchModal from "./SearchModal";

const HomeRootView = () => {
  const dispatch = useDispatch();
  const { searchValue, setSearchValue, searchInputRef } = useSearch();
  const {
    setFilterButtonPlaceHolder,
    setSearchBarPlaceHolder,
    rootActiveView,
    setRootActiveView,
  } = useOutletContext();
  const [topLeft, setTopLeft] = useState();
  const [rootResultCount, setRootResultCount] = useState();
  const [rootResultFetching, setRootResultFetching] = useState(false);
  const [rootResult, setRootResult] = useState();
  const location = useLocation();

  useEffect(() => {
    if (rootResult) {
      dispatch(
        filterAppsDataActions.setFilterAppDataSorted(rootResult.results),
      );
    }
  }, [rootResult, dispatch]);

  const [activeButtonHidStar, setActiveButtonHidStar] = useState<
    "hidden" | "starred" | null
  >(null);
  const { filterAppDataSorted } = useSelector(getFilterAppsData);

  const { filterSort, filterGroup } = useSelector(getFilterAppsState);

  const [activeType, setActiveType] = useState<string>("");
  const sortDirection = useSelector(
    (state: any) => state.filterApps.sortDirection,
  );

  const elementRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleElementIndex, setVisibleElementIndex] = useState<number | null>(
    null,
  );

  const uniqueFilterAppDataSorted = useMemo(() => {
    const uniqueSet = new Set(filterAppDataSorted.map((app) => app.id));
    return filterAppDataSorted.filter((app) => {
      if (uniqueSet.has(app.id)) {
        uniqueSet.delete(app.id);
        return true;
      }
      return false;
    });
  }, [filterAppDataSorted]);

  const onViewChange = (index: number, inView: boolean) => {
    if (inView) {
      setVisibleElementIndex(index);
    }
  };

  const [isZoomPopupOpen, setIsZoomPopupOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 650 && isZoomPopupOpen) {
        setIsZoomPopupOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isZoomPopupOpen]);

  const handleSortDirectionChange = (direction: "asc" | "desc") => {
    dispatch(filterAppsActions.setSortDirection(direction));
  };

  const order = useMemo(() => {
    return filterSort !== "" && filterGroup !== ""
      ? `${sortDirection === "desc" ? "-" : ""}${filterSort},${filterGroup}`
      : (filterSort !== "" &&
          `${sortDirection === "desc" ? "-" : ""}${filterSort}`) ||
          (filterGroup !== "" &&
            `${sortDirection === "desc" ? "-" : ""}${filterGroup}`) ||
          "";
  }, [filterSort, filterGroup, sortDirection]);

  // Monitor order changes and reset pagination when it changes
  const previousOrderRef = useRef(order);
  useEffect(() => {
    if (previousOrderRef.current !== order) {
      // Reset pagination when order changes to ensure only page 1 is fetched
      dispatch(filterAppsActions.resetPageFilter());
      dispatch(filterAppsActions.resetPageFilterSidebar());
      dispatch(filterAppsDataActions.resetFilterAppDataSortedAll());
      dispatch(filterAppsDataActions.resetFilterAppDataSorted());
      dispatch(filterAppsDataActions.resetFilterAppData());

      // Update the previous ref
      previousOrderRef.current = order;
    }
  }, [order, dispatch]);

  useEffect(() => {
    if (location.pathname.startsWith("/search")) {
      setRootActiveView(ACTIVE_VIEW_SEARCH);
      dispatch(filterAppsDataActions.setRootActiveView(ACTIVE_VIEW_SEARCH));
    } else if (location.pathname.startsWith("/animation-explorer")) {
      setRootActiveView(ACTIVE_VIEW_ANIMATION);
      dispatch(filterAppsDataActions.setRootActiveView(ACTIVE_VIEW_ANIMATION));
    } else if (location.pathname.startsWith("/web-onboardings")) {
      setRootActiveView(ACTIVE_VIEW_WEB_ONBOARDING);
      dispatch(
        filterAppsDataActions.setRootActiveView(ACTIVE_VIEW_WEB_ONBOARDING),
      );
    } else if (location.pathname.startsWith("/saved-library")) {
      setRootActiveView(ACTIVE_VIEW_SAVED_MODE);
      dispatch(filterAppsDataActions.setRootActiveView(ACTIVE_VIEW_SAVED_MODE));
    } else if (location.pathname === "/") {
      setRootActiveView(ACTIVE_VIEW_HOME);
      dispatch(filterAppsDataActions.setRootActiveView(ACTIVE_VIEW_HOME));
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    setFilterButtonPlaceHolder(
      <FilterButton
        isFetching={rootResultFetching}
        apiDataFilter={rootResult}
        isNext={!!rootResult?.next}
        setSortDirection={handleSortDirectionChange}
        activeType={activeType}
        setActiveType={setActiveType}
        uniqueFilterAppDataSorted={uniqueFilterAppDataSorted}
        activeButtonHidStar={activeButtonHidStar}
        setActiveButtonHidStar={setActiveButtonHidStar}
        rootActiveView={rootActiveView}
      />,
    );
    return () => {
      setFilterButtonPlaceHolder(<></>);
    };
  }, [rootResult, rootResultFetching, uniqueFilterAppDataSorted, order]);

  return (
    <div className={ExploreAppsStyles.homeRootView}>
      <div>
        <OnboardingModal />

        <Outlet
          context={{
            topLeft: topLeft,
            setTopLeft: setTopLeft,
            setRootResult: setRootResult,
            setRootResultCount: setRootResultCount,
            setRootResultFetching: setRootResultFetching,
            setSearchValue: setSearchValue,
            searchValue: searchValue,
            sortDirection: sortDirection,
            setSortDirection: handleSortDirectionChange,
            order: order,
            elementRefs: elementRefs,
            onViewChange: onViewChange,
            setRootActiveView: setRootActiveView,
            searchInputRef: searchInputRef,
          }}
        />
      </div>
    </div>
  );
};

export { HomeRootView };
